import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Field2 as Field,
  VibeModal,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import './HdmiSettings.scss';

const OVERRIDE_HELP_TEXT = 'Override hardware defaults. This may cause content to stretch, crop, or misalign.';
const RESTORE_WARNING_TEXT = 'Turning off the override resets this display to standard algorithms. '
  + 'If this screen was overridden to fix a legacy controller glitch, those display issues may return.';

function toDisplayValue(value) {
  return value === 0 ? '' : (value ?? '');
}

function HdmiSettings({
  className = '',
  displaySettings: displaySettingsProp,
  canEdit = false,
  disableInput = false,
  onUpdate = () => {},
}) {
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const isDisabled = !canEdit || disableInput;

  const displaySettings = displaySettingsProp || {};
  const displayInterfaceOverrideEnabled = Boolean(
    displaySettings.displayInterfaceOverrideEnabled,
  );
  const displayInterfaceOverrideResolution = displaySettings.displayInterfaceOverrideResolution
    || ['', ''];

  const overrideWidth = toDisplayValue(displayInterfaceOverrideResolution[0]);
  const overrideHeight = toDisplayValue(displayInterfaceOverrideResolution[1]);

  const patchDisplaySettings = (patch) => {
    onUpdate({
      displaySettings: {
        ...displaySettings,
        ...patch,
      },
    });
  };

  const onToggleOverride = (toggled) => {
    if (!toggled) {
      setShowRestoreModal(true);
      return;
    }

    patchDisplaySettings({
      displayInterfaceOverrideEnabled: true,
    });
  };

  const onConfirmRestore = () => {
    patchDisplaySettings({
      displayInterfaceOverrideEnabled: false,
    });
    setShowRestoreModal(false);
  };

  const onCloseRestore = () => {
    setShowRestoreModal(false);
  };

  const onChangeOverrideResolution = (index) => (e) => {
    const next = [...displayInterfaceOverrideResolution];
    next[index] = e.target.value;
    patchDisplaySettings({
      displayInterfaceOverrideResolution: next,
    });
  };

  return (
    <div
      className={classNames('HdmiSettings', className)}
    >
      <div className="hdmi-settings-header">
        <div className="hdmi-settings-title">
          HDMI Settings
        </div>
      </div>

      <Grid spacing={2} container>
        <Grid size={{ xs: 12 }}>
          <Field
            type="toggle"
            label="HDMI Interface"
            toggleProps={{
              label: 'Override HDMI Defaults',
              helpText: OVERRIDE_HELP_TEXT,
              helpTextStyle: {
                fontStyle: 'italic',
                fontWeight: 'normal',
              },
            }}
            value={displayInterfaceOverrideEnabled
              ? 'on'
              : 'off'}
            disabled={isDisabled}
            tabIndex={20}
            testId="location-hdmi-override-toggle"
            onChange={onToggleOverride}
          />
        </Grid>

        {displayInterfaceOverrideEnabled && (
          <>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Resolution Width"
                name="displayInterfaceOverrideWidth"
                innerLabelProps={{
                  text: 'px',
                }}
                value={overrideWidth}
                onChange={onChangeOverrideResolution(0)}
                disabled={isDisabled}
                required
                tabIndex={21}
                testId="location-hdmi-override-width-input"
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Resolution Height"
                name="displayInterfaceOverrideHeight"
                innerLabelProps={{
                  text: 'px',
                }}
                value={overrideHeight}
                onChange={onChangeOverrideResolution(1)}
                disabled={isDisabled}
                required
                tabIndex={22}
                testId="location-hdmi-override-height-input"
              />
            </Grid>
          </>
        )}
      </Grid>

      <VibeModal
        show={showRestoreModal}
        type="confirm"
        title="Restore Display Settings"
        confirmProps={{
          text: 'Ok',
        }}
        testIds={{
          root: 'location-hdmi-restore-dialog',
          confirm: 'location-hdmi-restore-confirm-button',
          cancel: 'location-hdmi-restore-cancel-button',
        }}
        onConfirm={onConfirmRestore}
        onClose={onCloseRestore}
      >
        {RESTORE_WARNING_TEXT}
      </VibeModal>
    </div>
  );
}

const numericPair = PropTypes.arrayOf(PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.string,
]));

HdmiSettings.propTypes = {
  className: PropTypes.string,
  displaySettings: PropTypes.shape({
    displayInterfaceOverrideEnabled: PropTypes.bool,
    displayInterfaceOverrideResolution: numericPair,
  }),
  canEdit: PropTypes.bool,
  disableInput: PropTypes.bool,
  onUpdate: PropTypes.func,
};

export default HdmiSettings;
